import { Controller } from "@hotwired/stimulus"

// Handles drag-and-drop for resources in Plantafel
export default class extends Controller {
  static values = {
    draggable: { type: Boolean, default: true },
  }

  connect() {
    if (this.draggableValue) {
      // Store the bound refs: removeEventListener only matches the exact function added, so a
      // fresh .bind() in disconnect() would never remove anything and listeners would pile up
      // across Turbo renders.
      this.onDragStart = this.handleDragStart.bind(this)
      this.onDragEnd = this.handleDragEnd.bind(this)
      this.element.addEventListener("dragstart", this.onDragStart)
      this.element.addEventListener("dragend", this.onDragEnd)
    }
  }

  disconnect() {
    if (this.onDragStart) this.element.removeEventListener("dragstart", this.onDragStart)
    if (this.onDragEnd) this.element.removeEventListener("dragend", this.onDragEnd)
  }

  handleDragStart(event) {
    if (!this.draggableValue) {
      event.preventDefault()
      return
    }

    const resourceId = this.element.dataset.resourceId
    const resourceType = this.element.dataset.resourceType
    const resourceName = this.element.dataset.resourceName

    // Multi-select: if this card is one of several ticked employees, drag the whole set so the
    // drop creates one allocation per employee. Dragging a non-ticked card stays a single drag
    // and leaves the selection untouched.
    const payload = { resourceId, resourceType, resourceName }
    const selectedIds = this.selectedResourceIds()
    if (resourceType === "Employee" && selectedIds.length > 1 && selectedIds.includes(resourceId)) {
      // The dialog formats the "N Mitarbeiter" label itself from resourceIds (kept translatable).
      payload.resourceIds = selectedIds
    }

    // Combined vehicle+driver drag (a Befähigte-Mitarbeiter row under a fleet card): carry both
    // the fleet and the employee so the drop plans them together in one gesture.
    if (resourceType === "FleetCrew") {
      payload.fleetId = this.element.dataset.fleetId
      payload.employeeId = this.element.dataset.employeeId
    }

    // Set drag data
    event.dataTransfer.effectAllowed = "copy"
    event.dataTransfer.setData("application/json", JSON.stringify(payload))

    // Visual feedback
    this.element.classList.add("opacity-50", "scale-95")

    // Highlight valid drop zones
    this.highlightDropZones(true)
  }

  // Distinct employee ids currently ticked in the resource panel (an employee repeats across the
  // per-day columns, so dedupe). Read from the DOM so this controller stays decoupled from the panel.
  selectedResourceIds() {
    const ids = [...document.querySelectorAll('[data-resource-selected="true"]')].map(
      (el) => el.dataset.resourceId,
    )
    return [...new Set(ids.filter(Boolean))]
  }

  handleDragEnd(_event) {
    // Remove visual feedback
    this.element.classList.remove("opacity-50", "scale-95")

    // Remove drop zone highlights
    this.highlightDropZones(false)
  }

  highlightDropZones(highlight) {
    const dropZones = document.querySelectorAll("[data-drop-zone]")
    dropZones.forEach((zone) => {
      if (highlight) {
        zone.classList.add("bg-teal-50", "border", "border-dashed", "border-teal-300")
      } else {
        zone.classList.remove("bg-teal-50", "border", "border-dashed", "border-teal-300")
      }
    })
  }
}
